import { type App, ref } from 'vue';

import * as Sentry from '@sentry/vue';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  signIn as amplifySignIn,
  type SignInOutput,
  signOut,
  updatePassword
} from 'aws-amplify/auth';

import { getStoredTheme } from '@/composables/useAppTheme';
import { setUserProfile as setProfileState } from '@/composables/useUserProfile';
import { setUserSettings as setSettingsState } from '@/composables/useUserSettings';
import { logAppError, setSentryUserContext } from '@/config/sentryConfig';
import trackingEvents from '@/config/trackingEvents';
import router from '@/router';
import { AUTH_PAGE_NAMES } from '@/router/authPages';
import apiClient, {
  resetSigningOut,
  setMfaSetupInProgress
} from '@/services/api';
import { authHelpers } from '@/services/auth/auth-helper';
import {
  logout as logoutService,
  type LogoutReasonValue,
  performLogout
} from '@/services/auth/logoutService';
import { OWN_PROFILE_PATH } from '@/services/userProfile/constants';
import {
  clearUTMParams,
  getUTMParams
} from '@/services/utm/utmTrackingService';
import useAuthStore from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import useScopesStore from '@/stores/scopes';
import { clearIntercomUser, syncIntercomUser } from '@/utils/intercomUtils';
import { resetAllStores } from '@/utils/resetStores';

// Global MFA State

interface MfaState {
  showMFA: boolean;
  mfaMode: 'setup' | 'login' | null;
}

// These are outside useAuth to maintain state across all composable instances
const mfaSession = ref<SignInOutput | null>(null);
const mfaState = ref<MfaState>({ showMFA: false, mfaMode: null });

const getMFASession = () => mfaSession.value;

export const useAuth = (app?: unknown) => {
  const authStore = useAuthStore();
  const permissionsStore = usePermissionsStore();

  // Sign In Flow

  /**
   * Sign in with username and password
   *
   * Flow:
   * 1. Attempt sign-in with Amplify
   * 2. Check if MFA verification is required (user has MFA enabled)
   * 3. Check if password change is required (temporary password)
   * 4. Check if MFA setup is required (first-time MFA setup)
   * 5. If none of the above, initialize the app normally
   */
  const signIn = async (username: string, password: string) => {
    // We keep track of the step for error reporting
    let currentStep = 'initial_request';

    try {
      // Lift the signing-out gate so API requests are allowed again
      resetSigningOut();
      resetMFAState();

      const signInResult: SignInOutput = await amplifySignIn({
        username: username.toLowerCase(),
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' }
      });

      // Update the step based on Amplify's response
      currentStep = signInResult.nextStep.signInStep;

      // Scenario 1: User has MFA enabled, needs to enter TOTP code
      if (authHelpers.needsMFAVerification(signInResult)) {
        if (router.currentRoute.value.name !== 'login') {
          await router.push({ name: 'login' });
        }
        mfaSession.value = signInResult;
        mfaState.value = { showMFA: true, mfaMode: 'login' };
        return signInResult;
      }

      // Scenario 2: User has temporary password, must change it
      if (authHelpers.needsPasswordChange(signInResult)) {
        authStore.setPwdChangeUser(signInResult);
        await router.push({ name: 'forceChangePwd' });
        return signInResult;
      }

      // Scenario 3: User signed in, check if MFA setup is required
      if (signInResult.isSignedIn) {
        currentStep = 'fetching_attributes';

        const userAttributes = await fetchUserAttributes();

        if (authHelpers.needsMFASetup(userAttributes)) {
          currentStep = 'mfa_setup_required';

          mfaSession.value = signInResult;
          mfaState.value = { showMFA: true, mfaMode: 'setup' };
          if (router.currentRoute.value.name !== 'login') {
            await router.push({ name: 'login' });
          }
          return signInResult;
        }

        // Scenario 4: Normal login, initialize app
        currentStep = 'initializing_app';
        await initApp({ recover: true });
        return signInResult;
      }

      // Unhandled scenario
      console.warn('[signIn] Unhandled sign-in step:', signInResult.nextStep);
      return signInResult;
    } catch (error) {
      logAppError({
        error,
        functionName: 'signIn',
        metadata: {
          step: currentStep,
          userIdentified: !!username
        }
      });

      throw error;
    }
  };

  // MFA State Management

  /**
   * Get current MFA state
   */
  const getMFAState = () => mfaState.value;

  /**
   * Reset MFA state to initial values
   */
  const resetMFAState = () => {
    mfaState.value = { showMFA: false, mfaMode: null };
    mfaSession.value = null;
  };

  /**
   * Cancel MFA process and sign out
   */
  const cancelMFA = async () => {
    resetMFAState();
    await signOut();
  };

  // MFA Completion Handlers

  /**
   * Complete MFA setup flow
   *
   * After user completes MFA setup:
   * 1. Verify user is authenticated
   * 2. Initialize app (attributes are already updated and session refreshed)
   * 3. Retry if needed (backend may need time to see updated JWT tokens)
   */
  const completeMFASetup = async () => {
    // Suppress the API interceptor's profile-403 auth-recovery/logout while the
    // backend propagates the freshly-updated JWT; the retry loop below handles
    // those 403s itself.
    setMfaSetupInProgress(true);
    try {
      await getCurrentUser();

      const maxRetries = 3;
      const retryDelay = 1000; // 1 second

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        const isFinalAttempt = attempt === maxRetries - 1;

        try {
          await initApp();
          resetMFAState();
          return true;
        } catch (error: any) {
          // Retry only a transient 403 (the freshly-updated JWT may not have
          // propagated yet). Anything else — a 5xx/network error, or a 403 on the
          // final attempt — is a genuine failure, so tear the half-session down
          // in place for a clean re-login.
          const shouldRetry =
            error?.response?.status === 403 && !isFinalAttempt;

          if (shouldRetry) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          } else {
            logAppError({
              error,
              section: 'mfa_setup_completion',
              functionName: 'completeMFASetup',
              metadata: {
                attemptNumber: attempt + 1,
                retryDelay
              }
            });

            await recoverHalfSession(error);
            throw error;
          }
        }
      }

      throw new Error('MFA setup attribute did not propagate in time');
    } catch (error: unknown) {
      logAppError({
        error,
        section: 'mfa_setup',
        functionName: 'completeMFASetup'
      });

      throw error;
    } finally {
      setMfaSetupInProgress(false);
    }
  };

  /**
   * Complete MFA verification flow
   *
   * After user verifies TOTP code during login:
   * 1. Verify user is authenticated
   * 2. Initialize app normally
   */
  const completeMFAVerification = async () => {
    try {
      await getCurrentUser();
      await initApp({ recover: true });
      resetMFAState();
      return true;
    } catch (error: unknown) {
      logAppError({
        error,
        functionName: 'completeMFAVerification'
      });

      throw error;
    }
  };

  /**
   * Logout user
   *
   * Wrapper around logoutService that adds Sentry tracking and event logging.
   *
   * Steps:
   * 1. Track logout event
   * 2. Perform core logout operations (signOut, storage clear, state reset)
   * 3. Navigate to login page
   *
   * Cleanup failures are logged as warnings but don't prevent logout completion.
   * Navigation to /login always succeeds to avoid leaving app in partially
   * signed-out state. This catch block is for unexpected errors only.
   */
  const logoutUser = async (reason?: LogoutReasonValue) => {
    try {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Starting logout flow',
        level: 'info'
      });

      trackingEvents.logout();

      await logoutService(app as App | undefined, reason);

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Logout completed',
        level: 'info'
      });
    } catch (error) {
      logAppError({
        error,
        functionName: 'logout'
      });

      // Unexpected logout error; ensure minimal cleanup and stay logged out
      clearIntercomUser();
      resetApp();
      throw error;
    }
  };

  // Password Management

  /**
   * Complete new password setup
   *
   * After user sets new password, check if MFA setup is required
   * Returns: { needsMFASetup: boolean, result: object }
   */
  const completeNewPassword = async (newPassword: string) => {
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });

      if (result.isSignedIn) {
        const userAttributes = await fetchUserAttributes();

        if (authHelpers.needsMFASetup(userAttributes)) {
          mfaState.value = { showMFA: true, mfaMode: 'setup' };
          if (router.currentRoute.value.name !== 'login') {
            await router.push({ name: 'login' });
          }
          return { needsMFASetup: true, result };
        }
      }

      return { needsMFASetup: false, result };
    } catch (error: unknown) {
      logAppError({
        error,
        functionName: 'completeNewPassword'
      });

      throw error;
    }
  };

  /**
   * Initiate forgot password flow
   */
  const forgotPassword = async (username: string) => {
    await resetPassword({ username: username.toLowerCase() });
  };

  /**
   * Complete forgot password flow
   */
  const forgotPasswordSubmit = async (
    username: string,
    code: string,
    password: string
  ) => {
    const normalizedUsername = username.toLowerCase();
    await confirmResetPassword({
      username: normalizedUsername,
      confirmationCode: code,
      newPassword: password
    });
    await signIn(normalizedUsername, password);
  };

  /**
   * Change password for authenticated user
   */
  const changePassword = async (oldPassword: string, newPassword: string) => {
    await updatePassword({ oldPassword, newPassword });
  };

  // User Profile & Permissions

  /**
   * Load user profile from backend
   */
  const loadBackOfficeUser = async () => {
    try {
      const { data } = await apiClient.get(OWN_PROFILE_PATH);
      return data;
    } catch (error: unknown) {
      logAppError({
        error,
        functionName: 'loadBackOfficeUser'
      });

      // Don't handle 401 here - the API interceptor already handles it with the
      // reason parameter. Re-throw to let error handling flow through properly.
      throw error;
    }
  };

  /**
   * Set user permissions in store
   */
  const setPermissions = (user) => {
    permissionsStore.hydrate(user.actions, user.tenants);
  };

  /**
   * Set user profile state and derive tracking roles
   */
  const setUserProfile = (user) => {
    setProfileState(user);

    // Extract and store user roles for tracking
    if (user?.permissions) {
      const roles = user.permissions.map(
        (p) => `${p.role.name} (${p.role.context})`
      );
      authStore.setUserRoles(roles);
    }

    setUserTracking(user);
  };

  /**
   * Set user settings state
   */
  const setUserSettings = (settings) => {
    setSettingsState(settings);
  };

  /**
   * Initialize user tracking
   *
   * Identifies user in Mixpanel and fires User event with optional campaign attribution.
   * Retrieves UTM parameters stored during initial page load and includes them in User event.
   * Uses fallback strategy: sessionStorage first, then localStorage (24-hour window).
   * See src/services/utm/utmTrackingService.ts for detailed retrieval logic.
   */
  const setUserTracking = (userProfile) => {
    if (userProfile?.id) {
      trackingEvents.identify(userProfile.id);

      // Retrieve campaign UTM parameters if available
      const utmData = getUTMParams();
      const utmParams =
        utmData?.params && Object.keys(utmData.params).length
          ? utmData.params
          : undefined;

      const theme = getStoredTheme();
      Sentry.setTag('ui.theme', theme);
      // Fire User event with optional UTM attribution
      trackingEvents.user(
        userProfile.id,
        userProfile.tenants.map((t) => t.name),
        undefined,
        utmParams,
        theme
      );

      // Fire Login event only for interactive auth flows (avoid counting session restore on app mount)
      if (AUTH_PAGE_NAMES.includes(String(router.currentRoute.value.name))) {
        trackingEvents.login();
      }

      // Cleanup - remove campaign data after processing
      // Prevents false attribution on subsequent logins from the same device
      clearUTMParams();
    }
  };

  // App Initialization

  /**
   * Recover from a "half-session": AWS Cognito login succeeded but the profile
   * (`/users/me`) load failed, leaving a valid session with no profile that
   * strands the user (a re-login would throw UserAlreadyAuthenticatedException).
   * Tear it down in place with performLogout (not logout — no redirect/reload,
   * so the caller's error dialog and credentials survive for a clean retry).
   *
   * Only the failures that cause a half-session recover here — a 5xx/network
   * error or a 403 on /users/me. A 401 is left to the API interceptor
   * (isSessionExpired), and the isAxiosError check ignores non-network init bugs.
   */
  const recoverHalfSession = async (error: any) => {
    const status = error?.response?.status;
    const isServerOrNetworkError =
      error?.isAxiosError === true && (status === undefined || status >= 500);
    const isPermissionDenied = status === 403;

    if (
      error?.isSessionExpired !== true &&
      (isServerOrNetworkError || isPermissionDenied)
    ) {
      // Return MFA-flow callers to a clean login form; no-op for normal login.
      resetMFAState();
      // No reason passed: performLogout does no redirect (that's what keeps the
      // caller's error dialog + credentials), so a LogoutReason would be inert.
      await performLogout(app as App | undefined);
    }
  };

  /**
   * Initialize application after authentication
   *
   * Flow:
   * 1. Fetch user attributes (Amplify v6 requires separate call)
   * 2. Check if MFA setup is required
   * 3. Load user profile from backend
   * 4. Set up permissions, profile, and settings
   * 5. Mark user as authenticated
   * 6. Redirect from auth pages to landing page
   *
   * @param recover - when true, run recoverHalfSession() on failure before
   *   rethrowing, so re-login starts clean. Off for the boot check and
   *   completeMFASetup, which retries transient 403s and so owns its own teardown.
   */
  const initApp = async ({ recover = false }: { recover?: boolean } = {}) => {
    // Lift the signing-out gate — initApp() is only reached when a valid
    // Amplify session exists, so API calls must be allowed regardless of
    // any stale cancelPendingRequests() call from a previous session.
    resetSigningOut();

    let userAttributes;
    let backOfficeUser;

    try {
      userAttributes = await fetchUserAttributes();

      // Inform Sentry of the logged in user
      await setSentryUserContext();

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Amplify attributes fetched',
        level: 'info'
      });

      // Check if MFA setup is required
      if (authHelpers.needsMFASetup(userAttributes)) {
        mfaState.value = { showMFA: true, mfaMode: 'setup' };
        authStore.setUserAuthenticated(false);
        await router.push({ name: 'login' });
        return;
      }

      // Mark user as authenticated BEFORE loading profile, so that if the profile API
      // returns 401, the API interceptor knows to logout with reason instead of skipping
      // the logout logic due to being on an unguarded route
      authStore.setUserAuthenticated(true);

      // Load user data and initialize app state
      backOfficeUser = await loadBackOfficeUser();

      Sentry.addBreadcrumb({
        category: 'api',
        message: 'Back Office profile loaded',
        level: 'info',
        data: { role: backOfficeUser.role } // Safe metadata
      });

      setUserProfile(backOfficeUser);
      setUserSettings(backOfficeUser.settings);

      Sentry.addBreadcrumb({
        category: 'state',
        message: 'Applying permissions and routing',
        level: 'info'
      });

      setPermissions(backOfficeUser);

      // Sync Intercom identity here (not in a component) so every auth path —
      // fresh login, MFA completion, session restore on app boot — funnels
      // through this one place once profile + permissions are hydrated.
      //
      // Isolated in its own try/catch: Intercom is non-critical, but it sits
      // inside initApp's try, whose catch forces a logout. A synchronous
      // failure syncing Intercom must not take the whole auth flow down with
      // it — log it and carry on.
      try {
        syncIntercomUser(backOfficeUser, {
          hasSingleContext: permissionsStore.hasSingleContext
        });
      } catch (error) {
        logAppError({
          error,
          functionName: 'initApp',
          section: 'intercom_sync',
          level: 'warning'
        });
      }

      // Redirect from auth pages to landing page
      if (AUTH_PAGE_NAMES.includes(String(router.currentRoute.value.name))) {
        await router.push({ name: 'landing' });
      }
    } catch (error: any) {
      // Log before recovering: performLogout() (inside recoverHalfSession)
      // clears the Sentry user context, so logging after it would drop the user
      // from the event.
      logAppError({
        error,
        functionName: 'initApp'
      });

      if (recover) {
        await recoverHalfSession(error);
      }

      throw error;
    }
  };

  /**
   * Check for existing authentication on app mount
   *
   * Returns: { status: 'authenticated' | 'mfa-setup-required' | 'unauthenticated', user? }
   */
  const checkExistingAuthOnMount = async () => {
    try {
      const user = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();

      if (authHelpers.needsMFASetup(userAttributes)) {
        mfaState.value = { showMFA: true, mfaMode: 'setup' };
        return { status: 'mfa-setup-required', user };
      }

      await initApp();
      return { status: 'authenticated', user };
    } catch {
      return { status: 'unauthenticated' };
    }
  };

  /**
   * Reset entire application state
   */
  const resetApp = () => {
    resetMFAState();
    resetAllStores();
    useScopesStore().resetTimezone();
    setProfileState(null);
    setSettingsState(null);
  };

  return {
    // Core authentication
    signIn,
    logoutUser,
    checkExistingAuthOnMount,

    // Password management
    completeNewPassword,
    forgotPassword,
    forgotPasswordSubmit,
    changePassword,

    // MFA flow control
    getMFAState,
    getMFASession,
    resetMFAState,
    cancelMFA,
    completeMFASetup,
    completeMFAVerification,

    // User profile & app state
    loadBackOfficeUser,
    setPermissions,
    setUserProfile,
    setUserSettings,
    initApp,
    resetApp
  };
};
